// ---------------------------------------------------------------------------
// MONEY PAGES — high-intent commercial landing pages.
//
//   /en/leads/roofing-leads-california
//   /en/leads/roofing-leads-riverside-ca
//   /en/leads/concrete-leads-riverside-county
//
// Generated from SEO_TRADES × (statewide + top metros + top counties) so the
// architecture scales to 50 trades × 200 cities × 58 counties without any new
// code. Single source of truth for routing, internal links and the sitemap.
// ---------------------------------------------------------------------------

import { MONEY_TRADES, SEO_TRADES, SeoTrade } from './trades';
import { COUNTIES, CITIES, TOP_METRO_SLUGS, TOP_COUNTY_SLUGS } from './locations';

export interface MoneyPage {
  /** URL slug under /leads/ */
  slug: string;
  trade: SeoTrade;
  /** 'state' | 'city' | 'county' */
  scope: 'state' | 'city' | 'county';
  /** Human place label: "California", "Riverside, CA", "Riverside County" */
  place: string;
  /** Link back into the location hierarchy. */
  citySlug?: string;
  countySlug?: string;
}

const build = (): MoneyPage[] => {
  const out: MoneyPage[] = [];

  // 1. Statewide pages for every money trade (highest volume queries).
  MONEY_TRADES.forEach((trade) => {
    out.push({ slug: `${trade.slug}-leads-california`, trade, scope: 'state', place: 'California' });
  });

  // 2. Trade × top metro (the core commercial inventory).
  const metros = TOP_METRO_SLUGS.map((slug) => CITIES.find((c) => c.slug === slug)).filter(Boolean) as typeof CITIES;
  MONEY_TRADES.forEach((trade) => {
    metros.forEach((city) => {
      out.push({
        slug: `${trade.slug}-leads-${city.slug}-ca`,
        trade,
        scope: 'city',
        place: `${city.name}, CA`,
        citySlug: city.slug,
        countySlug: city.county,
      });
    });
  });

  // 3. Trade × top county.
  const counties = TOP_COUNTY_SLUGS.map((slug) => COUNTIES.find((x) => x.slug === slug)).filter(Boolean) as typeof COUNTIES;
  MONEY_TRADES.forEach((trade) => {
    counties.forEach((county) => {
      out.push({
        slug: `${trade.slug}-leads-${county.slug}`,
        trade,
        scope: 'county',
        place: `${county.name} County`,
        countySlug: county.slug,
      });
    });
  });

  // 4. Buyer-type statewide pages (subcontractor / GC / commercial intent).
  ['general-contracting', 'commercial-construction', 'residential-construction'].forEach((slug) => {
    const trade = SEO_TRADES.find((t) => t.slug === slug);
    if (trade && !out.some((m) => m.slug === `${slug}-leads-california`)) {
      out.push({ slug: `${slug}-leads-california`, trade, scope: 'state', place: 'California' });
    }
  });

  return out;
};

export const MONEY_PAGES: MoneyPage[] = build();

export const moneyPageBySlug = (slug: string) => MONEY_PAGES.find((m) => m.slug === slug);

/** Related money pages for internal linking (same trade, different place). */
export const relatedMoneyPages = (page: MoneyPage, max = 10) =>
  MONEY_PAGES.filter((m) => m.trade.slug === page.trade.slug && m.slug !== page.slug).slice(0, max);

/** Other trades in the same place (cross-sell links, no orphan pages). */
export const siblingMoneyPages = (page: MoneyPage, max = 10) =>
  MONEY_PAGES.filter(
    (m) => m.slug !== page.slug && m.place === page.place && m.trade.slug !== page.trade.slug
  ).slice(0, max);
